import _ from 'lodash';
import {
  sqCapsuleRuntimeStore,
  sqTrendCapsuleStore,
  sqTrendConditionStore,
  sqTrendScalarStore,
  sqTrendSeriesStore,
  sqTrendStore,
  sqTrendTableStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { findItemIn, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { withChartRuntimeState } from '@/trendData/runtimeOverlays.utilities';
import { getCapsuleItems } from '@/trendData/trendCapsule.selectors';
import { ITEM_CHILDREN_TYPES, ITEM_TYPES, PREVIEW_ID, TREND_VIEWS } from '@/trendData/trendData.constants';
import { getCapsuleSeries } from '@/trendData/trendSeries.selectors';
import { removeUncertaintyIndicatorsFromChartItems } from '@/utilities/chartHelper.utilities';
import type { SeriesValues } from '@/trendData/seriesRuntime.store';
import type { CapsuleChartItem, ConditionItem, ScalarItem, TableItem } from '@/trendData/trendData.types';
import type { SeriesStoreItem } from '@/trendData/trendSeries.store';

// Union of all possible chart items that can appear in baseChartItems()
/** A series as the chart receives it: the item's config with its runtime render data overlaid */
export type ChartSeriesItem = SeriesStoreItem &
  Omit<SeriesValues, 'zones'> & {
    zones?: Array<{ color?: string }>;
  };

export type BaseChartItem = ChartSeriesItem | ScalarItem | ConditionItem | TableItem | CapsuleChartItem;

// Union of items that actually display on the chart (ConditionItem filtered out)
export type DisplayedChartItem = ChartSeriesItem | ScalarItem | TableItem | CapsuleChartItem;

/**
 * All the items that could be included depending on the view
 */
export function baseChartItems(): BaseChartItem[] {
  const commonItems: ConditionItem[][] = [
    // Condition items aren't really 'chartItems' since the capsule store provides the capsule items for the chart
    // However, `chartItems` isn't populated until late in the load of the page, so we test if a condition is
    // displayed accurately early in the loading of the page. They are filtered out before being returned by
    // chartItems()
    sqTrendConditionStore.items,
  ];
  if (sqTrendStore.view === TREND_VIEWS.CAPSULE) {
    const itemsToFlatten = [
      getCapsuleSeries(),
      _.filter(sqTrendScalarStore.items, (item) => !item.isChildOf),
      ...commonItems,
    ];
    return itemsToFlatten.flat();
  }

  const itemsToFlatten = [
    sqTrendTableStore.tables,
    groupedNonCapsuleSeries({
      nonCapsuleSeries: sqTrendSeriesStore.nonCapsuleSeriesAndPreview,
    }),
    getSqTrendCapsuleStoreChartItems(),
    sqTrendScalarStore.items,
    ...commonItems,
  ];
  return itemsToFlatten.flat();
}

/**
 * Helper function that helps hide not selected capsules from trend when we are in Chain View
 */
function getSqTrendCapsuleStoreChartItems() {
  const chartItems = sqCapsuleRuntimeStore.getState().chartItems;
  return sqTrendStore.view === TREND_VIEWS.CHAIN &&
    sqWorksheetStore.capsuleGroupMode &&
    chartItems.some((item) => item.selected)
    ? chartItems.filter((item) => item.selected)
    : chartItems;
}

/**
 * Helper function to filter sqTrendSeriesStore.nonCapsuleSeries in case of grouping mode.
 *
 * @param {Array} nonCapsuleSeries - signals that should be filtered if any grouping
 */
export function groupedNonCapsuleSeries({
  nonCapsuleSeries,
}: {
  nonCapsuleSeries: SeriesStoreItem[];
}): SeriesStoreItem[] {
  if (sqTrendStore.view === TREND_VIEWS.CHAIN && sqWorksheetStore.capsuleGroupMode) {
    const capsules = getCapsuleItems();
    const selectedCapsules = capsules.filter((capsule) => capsule.selected);
    const conditionsIds = [
      ...new Set((selectedCapsules.length ? selectedCapsules : capsules).map((capsule) => capsule.isChildOf)),
    ];
    const seriesIds = _.chain(conditionsIds)
      .flatMap((conditionId) => sqWorksheetStore.conditionToSeriesGrouping[conditionId])
      .compact()
      .uniq()
      .value();

    // in order to display shadedArea signals we are using item['isChildOf'] for filtering nonCapsuleSeries
    return _.filter(
      nonCapsuleSeries,
      (item) => _.includes(seriesIds, item.id) || _.includes(seriesIds, item.isChildOf),
    );
  }

  return nonCapsuleSeries;
}

/**
 * @returns the list of items that will be shown on the chart
 */
export function chartItems(): DisplayedChartItem[] {
  let chartItems: BaseChartItem[] = baseChartItems();

  if (sqTrendStore.hideUnselectedItems && _.some(chartItems, (item) => shownAsSelected(item))) {
    chartItems = _.chain(chartItems)
      .filter((item) =>
        shownInHideUnselectedItemsMode({
          item,
        }),
      )
      .value();
  }

  // Conditions are filtered out here because only capsules show on the chart
  let displayedItems: DisplayedChartItem[] = chartItems
    .filter((item): item is DisplayedChartItem => item.itemType !== ITEM_TYPES.CONDITION)
    .map(withChartRuntimeState);

  if (sqTrendStore.hideUncertainty) {
    displayedItems = removeUncertaintyIndicatorsFromChartItems(displayedItems);
  }

  return displayedItems;
}

/**
 * Test if an item that would be shown on the trend is currently hidden because of `hideUnselectedItems` mode
 */
export function isHidden({ item }: { item: any }) {
  if (!sqTrendStore.hideUnselectedItems) {
    return false;
  }
  const baseChartItemsArray = baseChartItems();

  return (
    sqTrendStore.hideUnselectedItems &&
    // If it isn't possible to be part of chartItems return false
    _.some(baseChartItemsArray, ['id', item.id]) &&
    // Items are only hidden if at least one of them is selected
    _.some(baseChartItemsArray, (chartItem) => shownAsSelected(chartItem)) &&
    // Finally, check if this item is shown
    !shownInHideUnselectedItemsMode({ item })
  );
}

/**
 * Helper function that determines if an item should be shown in hideUnselectedItems mode. Preview items are always
 * shown in the mode, but they do not cause items to be filtered
 */
function shownInHideUnselectedItemsMode({ item }: { item: any }) {
  return (
    item.id === PREVIEW_ID ||
    item.conditionId === PREVIEW_ID ||
    shownAsSelected(item) ||
    noneOfTypeSelected({ item }) ||
    _.startsWith(item.id, PREVIEW_ID) ||
    (sqTrendCapsuleStore.editingId && item.conditionId === sqTrendCapsuleStore.editingId)
  );
}

/**
 * Helper function for determining if an item should trigger hiding items in hideUnselectedItems mode
 */
export function shownAsSelected(item: any) {
  // Capsule series draw their selection from capsules but in this mode we want to hide them if their parent is
  // selected in the details pane. Scalars will still have "selected" set to true if they are selected.
  if (_.get(item, 'childType') === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE) {
    const parent = findItemIn(getTrendStores(), item.isChildOf);
    return _.get(parent, 'selected', false);
  }

  return _.get(item, 'selected', false);
}

/**
 * Helper function for determining if an item should display in capsule time in hideUnselectedItems mode, where if
 * no items of a given type(conditions vs. all other types) are selected all should be displayed, as in calendar time,
 * non-dimming.  Without this, it can be confusing to the user why nothing is displayed in the trend when
 * conditions are selected since capsules aren't chart items in capsule view.
 */
export function noneOfTypeSelected({ item }: { item: any }) {
  const selectedConditions = _.some(baseChartItems(), { itemType: ITEM_TYPES.CONDITION, selected: true });
  const selectedOthers = _.chain(sqTrendSeriesStore.nonCapsuleSeriesAndPreview as BaseChartItem[])
    .concat(sqTrendScalarStore.items as BaseChartItem[])
    .some({ selected: true })
    .value();

  if (sqTrendStore.view === TREND_VIEWS.CAPSULE) {
    return item.itemType === ITEM_TYPES.CONDITION ? !selectedConditions : !selectedOthers;
  }

  return false;
}
